import React from "react";
import { ListChecks, Sparkles } from "lucide-react";
import OverviewCard from "./OverviewCard";
import DeliveryItem from "./DeliveryItem";
import EmptyState from "./EmptyState";
import type { DeliveryItem as DeliveryItemData } from "./utils/deliveryChecklist";

interface DeliveryChecklistCardProps {
  items: DeliveryItemData[];
  /** Asks TACO to produce a deliverable. */
  onAsk: (item: DeliveryItemData) => void;
}

/**
 * The deliverables TACO suggests would make this project a strong package.
 *
 * Framed as suggestions rather than a definition of done, because that is what
 * they are: the agent proposes the set during onboarding from what it knows so
 * far, and is instructed to ask the user to review it. Stating it as settled
 * would overclaim — but the framing still has to encourage producing the
 * outstanding items, which is the point of the card.
 *
 * The review invitation is otherwise buried in a markdown section inside the
 * Summary tab; the note at the top of this card surfaces it.
 *
 * Always rendered, including before TACO has proposed anything. It used to hide
 * itself when empty, on the reasoning that an empty state would imply a blank
 * definition. That no longer holds: the set now lives only in the stored delivery
 * definition, so having none is simply the starting state of every project, and a
 * card that appears out of nowhere partway through is more confusing than one that
 * says what will land in it.
 */
const DeliveryChecklistCard: React.FC<DeliveryChecklistCardProps> = ({
  items,
  onAsk,
}) => {
  const hasItems = items.length > 0;
  const doneCount = items.filter((i) => i.isDone).length;
  const percent = hasItems ? Math.round((doneCount / items.length) * 100) : 0;

  return (
    <OverviewCard
      icon={<ListChecks className="h-3.5 w-3.5" />}
      title="What a strong delivery could include"
      count={hasItems ? `${doneCount} / ${items.length}` : undefined}
      beforeBody={
        // Only meaningful with items: a 0% bar reads as failure, and "suggested
        // from the project so far" is untrue when nothing has been suggested.
        !hasItems ? undefined : (
          // One padded block so the bar has air above and below rather than being
          // pinched between the header rule and the note.
          <div className="space-y-3 px-4 pb-1 pt-3.5">
            <div
              className="h-[5px] overflow-hidden rounded-full bg-accent"
              role="progressbar"
              aria-valuenow={doneCount}
              aria-valuemin={0}
              aria-valuemax={items.length}
              aria-label="Suggested deliverables produced"
            >
              <span
                className="block h-full rounded-full bg-green-700 transition-[width] duration-500 dark:bg-green-500"
                style={{ width: `${percent}%` }}
              />
            </div>

            <div className="flex items-start gap-2 rounded-lg border border-dashed border-border px-2.5 py-2 text-[11.5px] leading-snug text-muted-foreground">
              <Sparkles className="mt-px h-3.5 w-3.5 flex-none text-blue-600 dark:text-blue-400" />
              <span>
                <span className="font-semibold text-foreground">
                  Suggested by TACO
                </span>{" "}
                from the project so far. Worth building toward — and worth
                adjusting: tell TACO in chat what this delivery should include.
              </span>
            </div>
          </div>
        )
      }
    >
      {hasItems ? (
        items.map((item, index) => (
          <DeliveryItem
            // Labels are not unique: neither the prose parser nor the agent's
            // structured definition prevents two items sharing one.
            key={`${item.label}-${index}`}
            item={item}
            onAsk={onAsk}
          />
        ))
      ) : (
        <EmptyState>
          TACO will suggest what a strong delivery should include once it
          understands the project. You can also tell it in chat what this one
          needs.
        </EmptyState>
      )}
    </OverviewCard>
  );
};

export default DeliveryChecklistCard;
